<template>
  <div class="container">
    <!-- Help text and instructions -->
    <div class="help-section mb-4">
      <h2 class="title is-4 mb-2">Fund Similarity Tool</h2>
      <div class="notification is-light is-info">
        <ul>
          <li>Only works with funds. Results only include funds with at least $10B in assets.</li>
          <li>Similarity score indicates overall match based on weighted comparison of key fund characteristics.</li>
          <li>Results are from the same morningstar style category as your searched fund.</li>
          <li><strong>Use the filter inputs below each column header to filter results.</strong></li>
        </ul>
      </div>
    </div>
    
    <div class="is-flex is-justify-content-space-between is-align-items-center mb-4">
      <div class="is-flex-grow-1 mr-4">
        <b-input 
          v-model="searchQuery" 
          placeholder="Enter a mutual fund or ETF ticker symbol..." 
          class="search-box"
          @keyup.enter.native="fetchData" />
      </div>
      <div class="is-flex">
        <b-button @click="fetchData" class="mr-2" :disabled="isLoading">
          {{ isLoading ? 'Loading...' : 'Search' }}
        </b-button>
        <b-button @click="clearAllFilters" class="mr-2" :disabled="!hasActiveFilters" type="is-warning">
          Clear Filters
        </b-button>
        <b-button 
          @click="exportToCsv" 
          :disabled="!filteredData || !filteredData.length"
          type="is-success">
          Export to CSV
        </b-button>
      </div>
    </div>
    
    <b-loading :is-active="isLoading" :full-page="false"></b-loading>
    
    <div class="table-container">
      <div class="table-wrapper" v-if="!isLoading && tickers && tickers.length">
        <table class="table is-striped is-hoverable is-fullwidth">
          <!-- Group Headers Row -->
          <thead>
            <tr class="group-header-row">
              <th v-for="(group, index) in columnGroups" :key="'group-' + index" 
                  :colspan="group.columns.length" 
                  class="has-text-centered group-header">
                {{ group.name }}
              </th>
            </tr>
            <!-- Column Headers Row -->
            <tr>
              <th v-for="(column, index) in flatColumns" :key="'col-' + index">
                {{ column.label }}
              </th>
            </tr>
            <!-- Filter Row -->
            <tr class="filter-row">
              <th v-for="(column, index) in flatColumns" :key="'filter-' + index" class="filter-cell">
                <div v-if="isNumericColumn(column.field)" class="numeric-filter">
                  <input 
                    v-model="columnFilters[column.field].min"
                    type="number" 
                    :placeholder="`Min ${column.label}`"
                    class="filter-input numeric-input"
                    @input="applyFilters"
                    step="any">
                  <input 
                    v-model="columnFilters[column.field].max"
                    type="number" 
                    :placeholder="`Max ${column.label}`"
                    class="filter-input numeric-input"
                    @input="applyFilters"
                    step="any">
                </div>
                <input 
                  v-else
                  v-model="columnFilters[column.field].text"
                  type="text" 
                  :placeholder="`Filter ${column.label}`"
                  class="filter-input"
                  @input="applyFilters">
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rowIndex) in filteredData" :key="'row-' + rowIndex">
              <td v-for="(column, colIndex) in flatColumns" :key="'cell-' + rowIndex + '-' + colIndex">
                {{ row[column.field] }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    
    <div v-if="!isLoading && tickers && tickers.length" class="results-info mt-3">
      <p class="has-text-grey">
        Showing {{ filteredData.length }} of {{ tickers.length }} results
        <span v-if="hasActiveFilters">(filtered)</span>
      </p>
    </div>
    
    <p v-if="!isLoading && hasSearched && (!tickers || tickers.length === 0)">
      No results found for "{{ lastSearchedQuery }}". Please make sure you are searching for a mutual fund or ETF ticker symbol.
    </p>
  </div>
</template>

<script>
export default {
  data() {
    return {
      searchQuery: '',
      tickers: [],
      isLoading: false,
      hasSearched: false,
      lastSearchedQuery: '',
      columnFilters: {},
      
      // Column group definitions
      columnGroupDefinitions: [
        {
          name: 'Basic Information',
          columns: ['Ticker', 'Name', 'Fund Family', 'Similarity', 'Asset Type', '# Holdings', 'Yield', 'Pct Equity', 'Turnover']
        },
        {
          name: 'Market Cap',
          columns: ['Small Cap', 'Mid Cap', 'Large Cap', 'Mega Cap']
        },
        {
          name: 'Geography',
          columns: ['Emerging', 'Developed', 'United States']
        },
        {
          name: 'Style Category',
          columns: ['Small Value', 'Small Growth', 'Small Core', 'Mid Value', 'Mid Growth', 'Mid Core',
                   'Large Value', 'Large Growth', 'Large Core']
        },
        {
          name: 'Fixed Income Type',
          columns: ['Corporate', 'Municipal', 'Government', 'Securitized']
        },
        {
          name: 'Maturity',
          columns: ['< 1 Year', '1-3 Years', '3-5 Years', '5-7 Years', '7-10 Years', 
                   '10-15 Years', '15-20 Years', '20-30 Years', '>30 Years']
        }
      ]
    };
  },
  computed: {
    filteredData() {
      if (!this.tickers || !this.tickers.length) return [];
      
      return this.tickers.filter(row => {
        return this.flatColumns.every(column => {
          const filter = this.columnFilters[column.field];
          if (!filter) return true;
          
          const value = row[column.field];
          
          if (this.isNumericColumn(column.field)) {
            const numValue = this.parseNumericValue(value);
            if (numValue === null) return true; // Skip filtering if can't parse
            
            const min = filter.min !== null && filter.min !== '' ? parseFloat(filter.min) : null;
            const max = filter.max !== null && filter.max !== '' ? parseFloat(filter.max) : null;
            
            if (min !== null && numValue < min) return false;
            if (max !== null && numValue > max) return false;
            
            return true;
          } else {
            if (!filter.text || filter.text.trim() === '') return true;
            const searchText = filter.text.toLowerCase();
            const cellValue = (value || '').toString().toLowerCase();
            return cellValue.includes(searchText);
          }
        });
      });
    },
    
    // Filter column groups to only include columns that exist in the data
    columnGroups() {
      if (!this.tickers || !this.tickers.length) return [];
      
      const availableColumns = Object.keys(this.tickers[0]);
      
      return this.columnGroupDefinitions
        .map(group => {
          const existingColumns = group.columns.filter(col => availableColumns.includes(col));
          return {
            name: group.name,
            columns: existingColumns.map(col => ({ field: col, label: col }))
          };
        })
        .filter(group => group.columns.length > 0);
    },
    
    // Flattened list of all columns for the table rows
    flatColumns() {
      return this.columnGroups.reduce((acc, group) => [...acc, ...group.columns], []);
    },
    
    hasActiveFilters() {
      return Object.values(this.columnFilters).some(filter => {
        if (filter.text && filter.text.trim() !== '') return true;
        if (filter.min !== null && filter.min !== '') return true;
        if (filter.max !== null && filter.max !== '') return true;
        return false;
      });
    }
  },
  watch: {
    flatColumns: {
      handler() {
        this.initializeFilters();
      },
      immediate: true
    }
  },
  methods: {
    async fetchData() {
      if (!this.searchQuery.trim()) {
        alert('Please enter a ticker symbol');
        return;
      }
      
      const uppercaseQuery = this.searchQuery.trim().toUpperCase();
      
      this.isLoading = true;
      this.tickers = [];
      try {
        const response = await fetch(`/api/v1/ticker_similarity?symbol=${encodeURIComponent(uppercaseQuery)}`);
        if (!response.ok) throw new Error('Failed to fetch data');
        const data = await response.json();

        if (Array.isArray(data) && data.length > 0) {
          this.tickers = data;
        } else {
          console.warn("Received empty or unexpected data structure:", data);
          this.tickers = [];
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        this.tickers = [];
      } finally {
        this.isLoading = false;
        this.hasSearched = true;
        this.lastSearchedQuery = uppercaseQuery;
      }
    },

    initializeFilters() {
      const newFilters = {};
      this.flatColumns.forEach(column => {
        if (this.isNumericColumn(column.field)) {
          newFilters[column.field] = { min: null, max: null };
        } else {
          newFilters[column.field] = { text: '' };
        }
      });
      this.columnFilters = newFilters;
    },

    isNumericColumn(fieldName) {
      // Define which columns should be treated as numeric
      const numericFields = [
        'Similarity', '# Holdings', 'Yield', 'Pct Equity', 'Turnover',
        'Small Cap', 'Mid Cap', 'Large Cap', 'Mega Cap',
        'Emerging', 'Developed', 'United States',
        'Small Value', 'Small Growth', 'Small Core', 'Mid Value', 'Mid Growth', 'Mid Core',
        'Large Value', 'Large Growth', 'Large Core',
        'Corporate', 'Municipal', 'Government', 'Securitized',
        '< 1 Year', '1-3 Years', '3-5 Years', '5-7 Years', '7-10 Years',
        '10-15 Years', '15-20 Years', '20-30 Years', '>30 Years'
      ];
      return numericFields.includes(fieldName);
    },

    parseNumericValue(value) {
      if (value === null || value === undefined || value === '') return null;
      
      // Remove common non-numeric characters like %, $, commas
      const cleanValue = value.toString().replace(/[$,%]/g, '');
      const numValue = parseFloat(cleanValue);
      
      return isNaN(numValue) ? null : numValue;
    },

    applyFilters() {
      // This method is called on input, the filtering happens automatically via computed property
      // We can add debouncing here if needed for performance
    },

    clearAllFilters() {
      this.initializeFilters();
    },

    exportToCsv() {
      if (!this.filteredData || !this.filteredData.length) return;

      const headers = this.flatColumns.map(col => col.label);
      const rows = this.filteredData.map(item => {
        return this.flatColumns.map(col => {
          const value = item[col.field] || '';
          return `"${value.toString().replace(/"/g, '""')}"`;
        });
      });

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `similar_tickers_${this.searchQuery}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }
};
</script>

<style scoped>
.container {
  max-width: 95%;
  margin: auto;
  padding: 20px;
}

.search-box {
  margin-bottom: 0;
}

.table-container {
  position: relative;
  overflow: hidden;
}

.table-wrapper {
  width: 100%;
  overflow: auto;
  max-height: 80vh;
}

.table-wrapper::-webkit-scrollbar {
  height: 8px;
  width: 8px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.is-flex {
  display: flex;
}

.is-justify-content-space-between {
  justify-content: space-between;
}

.is-align-items-center {
  align-items: center;
}

.is-flex-grow-1 {
  flex-grow: 1;
}

.mr-2 {
  margin-right: 0.5rem;
}

.mr-4 {
  margin-right: 1rem;
}

.mb-4 {
  margin-bottom: 1rem;
}

.mt-3 {
  margin-top: 0.75rem;
}

/* Help section styling */
.help-section .title {
  margin-bottom: 0.5rem;
}

.notification {
  padding: 1.25rem;
  border-radius: 4px;
}

.notification.is-light.is-info {
  background-color: #f0f8ff;
  color: #22509a;
}

.notification ul {
  margin-left: 1.5rem;
  margin-top: 0.5rem;
}

.notification li {
  margin-bottom: 0.25rem;
}

/* Group header styling */
.group-header-row {
  background-color: #f5f5f5;
}

.group-header {
  background-color: #eaeaea;
  border: 1px solid #dbdbdb;
  font-weight: bold;
  color: #363636;
  text-transform: uppercase;
  font-size: 0.8rem;
  padding: 0.5em 0.75em;
}

/* Filter row styling */
.filter-row {
  background-color: #f9f9f9;
}

.filter-cell {
  padding: 0.25em 0.5em !important;
  background-color: #f9f9f9;
}

.filter-input {
  width: 100%;
  padding: 0.25em 0.5em;
  border: 1px solid #dbdbdb;
  border-radius: 3px;
  font-size: 0.8rem;
  box-sizing: border-box;
}

.filter-input:focus {
  outline: none;
  border-color: #3273dc;
  box-shadow: 0 0 0 0.125em rgba(50, 115, 220, 0.25);
}

.numeric-filter {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.numeric-input {
  font-size: 0.75rem;
}

/* Table styling */
.table {
  border-collapse: collapse;
  width: 100%;
}

.table th, .table td {
  padding: 0.5em 0.75em;
  border: 1px solid #dbdbdb;
  white-space: nowrap;
}

.table tbody tr:hover {
  background-color: #f5f5f5 !important;
}

.results-info {
  font-size: 0.9rem;
}

.has-text-grey {
  color: #4a4a4a;
}
</style>